import { ReactNode, useState, useEffect, useRef, Suspense } from "react";
import { Alert, Box, Button, Divider, Grid, MenuItem, Skeleton, TextField, Typography } from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { isValidNumber } from "@utilities/validators/credentialValidator";
import CurrencySelectionHeader from "./Components/CurrencySelectionHeader";
import CurrencyListItem from "./Components/CurrencyListItem";
import PaymentButton from "@components/PaymentButton";
import { Constant } from "@constants/index";
import { getConvertedCurrency } from "@api/services/currencyConverterService";
import { DonationPaymentTypes, PaymentTypes } from "@enums/Payment";
import SelectDropdown from "@components/SelectDropdown";
import { ListData } from "@constants/listData";
import { APICaller, useAPICaller } from "@api/request";
import { getCampaignDetails } from "@api/services/campaignService";
import { SupportedOtherPayment } from "@enums/Campaign";
import { sendEvent } from "@utilities/googleAnalytics";
import { GoogleAnalyticsEvents } from "@constants/googleAnalyticsEvents";
import { ErrorMessages } from "@constants/errorMessages";
import { DonationType } from "@enums/Donation";
import { Select, FormControl, InputLabel } from "@mui/material";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

const PaymentMethod = ({
    campaignSlug,
    setDonationCurrency,
    setDonationAmount,
    setDonationAmountInBDT,
    setDonationType,
    setPaymentType,
    setDonationFrequency,
    setInvalidAmount,
    children,
}: {
    campaignSlug?: string;
    setDonationCurrency: any;
    setDonationAmount: any;
    setDonationAmountInBDT: any;
    setDonationType: any;
    setPaymentType: any;
    setDonationFrequency: any;
    setInvalidAmount?: any;
    children?: ReactNode;
}) => {
    const APICallerRef = useRef<APICaller>(useAPICaller());
    const donation = useSelector((state: any) => state.donation.donation);

    let amounts = [2500, 5000, 7000, 10000, 30000, 60000, 120000];
    const amountsByCurrency: Record<string, number[]> = {
        AUD: [35, 100, 150, 400, 800, 1500],
        BDT: [2500, 7000, 10000, 30000, 60000, 120000],
        CAD: [30, 80, 115, 350, 700, 1400],
        EUR: [20, 55, 80, 240, 480, 950],
        GBP: [20, 45, 70, 200, 400, 800],
        JPY: [3500, 8500, 12250, 37000, 75000, 150000],
        USD: [20, 60, 85, 250, 500, 1000],
        INR: [2000, 6000, 8000, 25000, 45000, 91000],
    };
    // Default to "INR"
    const [queryParams] = useSearchParams();
    const [currency, setCurrency] = useState<string>(ListData.CurrencyList[0].name);
    const selectedCurrencyCode = currency?.split(" ")[0] || "BDT";
    const selectedAmounts = amountsByCurrency[selectedCurrencyCode] || amountsByCurrency["BDT"];
    const [selectedAmount, setSelectedAmount] = useState<number | null>(amounts[3]); // ইনডেক্স ২-এর ভ্যালু সেট করা

    const [amountValue, setAmountValue] = useState<string>("2500");
    const [amountInBDT, setAmountInBDT] = useState<number | null>(null);
    const [currencyRate, setCurrencyRate] = useState<number>(1);
    const [oneTime, setOneTime] = useState<boolean>(false);
    const [recurring, setRecurring] = useState<boolean>(false);
    const [monthly, setMonthly] = useState<boolean>(false);
    const [yearly, setYearly] = useState<boolean>(false);
    const [daily, setDaily] = useState<boolean>(false);
    const [paymentMedia, setPaymentMedia] = useState<string>("");
    const isPaymentMethod = queryParams.get("payment_method");
    const isAutopay = queryParams.get("autopay");
    const [preferredBankPayment, setPreferredBankPayment] = useState<string>("");
    const [supportNexusPayment, setSupportNexusPayment] = useState<boolean>(false);
    const [supportAmexPayment, setSupportAmexPayment] = useState<boolean>(false);
    const [supportBkashPayment, setSupportBkashPayment] = useState<boolean>(false);
    const [supportNagadPayment, setSupportNagadPayment] = useState<boolean>(false);
    const [supportCashPayment, setSupportCashPayment] = useState<boolean>(false);
    const [supportCheckPayment, setSupportCheckPayment] = useState<boolean>(false);
    const [invalidAmountError, setInvalidAmountError] = useState<string>("");
    const [currencyError, setcurrencyError] = useState<string>("");
    const [recurringSupportedGateways, setRecurringSupportedGateways] = useState<PaymentTypes[]>([]);
    const [paymentTypeButtons, setPaymentTypeButtons] = useState<any[]>([
        {
            label: Constant.ONE_TIME,
            isSelected: oneTime,
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.OneTimePaymentButton);
                handleButtonClick("DonationType", Constant.ONE_TIME);
            },
        },
    ]);

    const currencyLabel = "Select Currency";

    let currencyDropdownContent: ReactNode[] = [];

    const donationNotesByCurrency: Record<string, Record<number, string>> = {
        BDT: {
            2500: "This contribution provides 1 bag blood transfusion for a poor patient.",
            7000: "This contribution provides 1 month of life-saving medicine for a poor patient.",
            10000: "This contribution provides treatment for a month for a poor patient.",
            30000: "This contribution provides treatment for 3 months for a poor patient.",
            60000: "This contribution provides treatment for 6 months for a poor patient.",
            120000: "This contribution provides treatment for a year for a poor patient.",
        },
        INR: {
            2000: "This contribution provides 1 bag blood transfusion for a poor patient.",
            6000: "This contribution provides 1 month of life-saving medicine for a poor patient.",
            8000: "This contribution provides treatment for a month for a poor patient.",
            25000: "This contribution provides treatment for 3 months for a poor patient.",
            45000: "This contribution provides treatment for 6 months for a poor patient.",
            91000: "This contribution provides treatment for a year for a poor patient.",
        },
        AUD: {
            35: "This contribution provides 1 bag blood transfusion for a poor patient.",
            100: "This contribution provides 1 month of life-saving medicine for a poor patient.",
            150: "This contribution provides treatment for a month for a poor patient.",
            400: "This contribution provides treatment for 3 months for a poor patient.",
            800: "This contribution provides treatment for 6 months for a poor patient.",
            1500: "This contribution provides treatment for a year for a poor patient.",
        },
        CAD: {
            30: "This contribution provides 1 bag blood transfusion for a poor patient.",
            80: "This contribution provides 1 month of life-saving medicine for a poor patient.",
            115: "This contribution provides treatment for a month for a poor patient.",
            350: "This contribution provides treatment for 3 months for a poor patient.",
            700: "This contribution provides treatment for 6 months for a poor patient.",
            1400: "This contribution provides treatment for a year for a poor patient.",
        },
        EUR: {
            20: "This contribution provides 1 bag blood transfusion for a poor patient.",
            55: "This contribution provides 1 month of life-saving medicine for a poor patient.",
            80: "This contribution provides treatment for a month for a poor patient.",
            240: "This contribution provides treatment for 3 months for a poor patient.",
            480: "This contribution provides treatment for 6 months for a poor patient.",
            950: "This contribution provides treatment for a year for a poor patient.",
        },
        GBP: {
            20: "This contribution provides 1 bag blood transfusion for a poor patient.",
            45: "This contribution provides 1 month of life-saving medicine for a poor patient.",
            70: "This contribution provides treatment for a month for a poor patient.",
            200: "This contribution provides treatment for 3 months for a poor patient.",
            400: "This contribution provides treatment for 6 months for a poor patient.",
            800: "This contribution provides treatment for a year for a poor patient.",
        },
        JPY: {
            3500: "This contribution provides 1 bag blood transfusion for a poor patient.",
            8500: "This contribution provides 1 month of life-saving medicine for a poor patient.",
            12250: "This contribution provides treatment for a month for a poor patient.",
            37000: "This contribution provides treatment for 3 months for a poor patient.",
            75000: "This contribution provides treatment for 6 months for a poor patient.",
            150000: "This contribution provides treatment for a year for a poor patient.",
        },
        USD: {
            20: "This contribution provides 1 bag blood transfusion for a poor patient.",
            60: "This contribution provides 1 month of life-saving medicine for a poor patient.",
            85: "This contribution provides treatment for a month for a poor patient.",
            250: "This contribution provides treatment for 3 months for a poor patient.",
            500: "This contribution provides treatment for 6 months for a poor patient.",
            1000: "This contribution provides treatment for a year for a poor patient.",
        },
        // Add more currencies as needed...
    };

    const getNote = (amount: any | null | undefined): string => {
        const selectedCurrencyCode = currency?.split(" ")[0] || "BDT"; // Extracts the currency code (default: BDT)
        const notes = donationNotesByCurrency[selectedCurrencyCode] || donationNotesByCurrency["BDT"];

        return amount && notes[amount] ? notes[amount] : "This contribution will sustain life-preserving care for thalassemia patients.";
    };

    const paymentFrequencyButtons = [
        {
            label: Constant.DAILY,
            isSelected: daily,

            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.RecurringPaymentButton);
                handleButtonClick("DonationType", Constant.RECURRING);
                sendEvent(GoogleAnalyticsEvents.DailyFrequencyButton);
                handleButtonClick("Frequency", Constant.DAILY);
            },
        },
        {
            label: Constant.MONTHLY,
            isSelected: monthly,
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.RecurringPaymentButton);
                handleButtonClick("DonationType", Constant.RECURRING);
                sendEvent(GoogleAnalyticsEvents.MonthlyFrequencyButton);
                handleButtonClick("Frequency", Constant.MONTHLY);
            },
        },
        {
            label: Constant.YEARLY,
            isSelected: yearly,
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.RecurringPaymentButton);
                handleButtonClick("DonationType", Constant.RECURRING);
                sendEvent(GoogleAnalyticsEvents.YearlyFrequencyButton);
                handleButtonClick("Frequency", Constant.YEARLY);
            },
        },
    ];

    // process.env.REACT_APP_BASE_NAME
    const recurringPaymentButtons = [
        {
            label: DonationPaymentTypes.Visa,
            iconPath: `/${process.env.REACT_APP_BASE_NAME}/static/images/logo/visa-logo.png`,

            isSelected: paymentMedia.toLowerCase() === DonationPaymentTypes.Visa.toLowerCase(),
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.PaymentTypeButton, { paymentType: DonationPaymentTypes.Visa });
                handleButtonClick("PaymentType", DonationPaymentTypes.Visa);
            },
        },
        {
            label: DonationPaymentTypes.Master,
            iconPath: `/${process.env.REACT_APP_BASE_NAME}/static/images/logo/mastercard-logo.png`,
            isSelected: paymentMedia.toLowerCase() === DonationPaymentTypes.Master.toLowerCase(),
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.PaymentTypeButton, { paymentType: DonationPaymentTypes.Master });
                handleButtonClick("PaymentType", DonationPaymentTypes.Master);
            },
        },
        {
            label: DonationPaymentTypes.BKASH,
            iconPath: `/${process.env.REACT_APP_BASE_NAME}/static/images/logo/bkash-logo.png`,
            isSelected: paymentMedia.toLowerCase() === DonationPaymentTypes.BKASH.toLowerCase(),
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.PaymentTypeButton, { paymentType: DonationPaymentTypes.BKASH });
                handleButtonClick("PaymentType", DonationPaymentTypes.BKASH);
            },
        },

        {
            label: DonationPaymentTypes.Amex,
            iconPath: `/${process.env.REACT_APP_BASE_NAME}/static/images/logo/amex-logo.png`,
            isSelected: paymentMedia.toLowerCase() === DonationPaymentTypes.Amex.toLowerCase(),
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.PaymentTypeButton, { paymentType: DonationPaymentTypes.Amex });
                handleButtonClick("PaymentType", DonationPaymentTypes.Amex);
            },
        },
        {
            label: DonationPaymentTypes.NAGAD,
            iconPath: `/${process.env.REACT_APP_BASE_NAME}/static/images/logo/nagad-logo.png`,
            isSelected: paymentMedia.toLowerCase() === DonationPaymentTypes.NAGAD.toLowerCase(),
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.PaymentTypeButton, { paymentType: DonationPaymentTypes.NAGAD });
                handleButtonClick("PaymentType", DonationPaymentTypes.NAGAD);
            },
        },
        {
            label: DonationPaymentTypes.NexusPay,
            iconPath: `/${process.env.REACT_APP_BASE_NAME}/static/images/logo/dbbl-nexus-pay-logo.png`,
            isSelected: paymentMedia.toLowerCase() === DonationPaymentTypes.NexusPay.toLowerCase(),
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.PaymentTypeButton, { paymentType: DonationPaymentTypes.NexusPay });
                handleButtonClick("PaymentType", DonationPaymentTypes.NexusPay);
            },
        },
    ];
    const oneTimePaymentButtons = [
        {
            label: DonationPaymentTypes.CASH,
            icon: <AttachMoneyIcon fontSize="small" />,
            isSelected: paymentMedia.toLowerCase() === DonationPaymentTypes.CASH.toLowerCase(),
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.PaymentTypeButton, { paymentType: DonationPaymentTypes.CASH });
                handleButtonClick("PaymentType", DonationPaymentTypes.CASH);
            },
        },
        {
            label: DonationPaymentTypes.CHEQUE,
            icon: <LocalOfferIcon fontSize="small" />,
            isSelected: paymentMedia.toLowerCase() === DonationPaymentTypes.CHEQUE.toLowerCase(),
            onClick: () => {
                sendEvent(GoogleAnalyticsEvents.PaymentTypeButton, { paymentType: DonationPaymentTypes.CHEQUE });
                handleButtonClick("PaymentType", DonationPaymentTypes.CHEQUE);
            },
        },
    ];

    currencyDropdownContent.push(<CurrencySelectionHeader key={0} title={currencyLabel} />);
    currencyDropdownContent.push(
        ListData.CurrencyList.map((item: { id: number; name: string }) => {
            return (
                <MenuItem key={item.id} value={item.name}>
                    <CurrencyListItem currencyName={item.name} />
                </MenuItem>
            );
        })
    );

    useEffect(() => {
        if (campaignSlug) {
            getCampaignData(campaignSlug);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignSlug]);
    // useEffect(() => {
    //     if (isPaymentMethod == "bkash") {
    //         handleButtonClick("PaymentType", DonationPaymentTypes.BKASH);
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [isPaymentMethod]);
    //   useEffect(() => {
    //     if (isAutopay == "bkash") {
    //          sendEvent(GoogleAnalyticsEvents.RecurringPaymentButton);
    //             handleButtonClick("DonationType", Constant.RECURRING);
    //             sendEvent(GoogleAnalyticsEvents.MonthlyFrequencyButton);
    //             handleButtonClick("Frequency", Constant.MONTHLY);
    //         handleButtonClick("PaymentType", DonationPaymentTypes.BKASH);
    //             setOneTime(false);
    //               setRecurring(true);
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [isAutopay]);

    // useEffect(() => {
    //     setOneTime(true); // Auto-select One-Time
    //     setRecurring(false);
    //     updatePaymentButtonSelection(Constant.ONE_TIME as DonationType);
    // }, []);
    useEffect(() => {
        if (isAutopay === "bkash") {
            // If autopay is bkash, select recurring only
            sendEvent(GoogleAnalyticsEvents.RecurringPaymentButton);
            handleButtonClick("DonationType", Constant.RECURRING);
            sendEvent(GoogleAnalyticsEvents.MonthlyFrequencyButton);
            handleButtonClick("Frequency", Constant.MONTHLY);
            handleButtonClick("PaymentType", DonationPaymentTypes.BKASH);
            setOneTime(false);
            setRecurring(true);
        } else if (isPaymentMethod === "bkash") {
            // If autopay is NOT bkash, but payment method is bkash, select one-time only
            handleButtonClick("PaymentType", DonationPaymentTypes.BKASH);
            setOneTime(true);
            setRecurring(false);
            updatePaymentButtonSelection(Constant.ONE_TIME as DonationType);
        } else {
            // Default: one-time selected
            setOneTime(true);
            setRecurring(false);
            updatePaymentButtonSelection(Constant.ONE_TIME as DonationType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAutopay, isPaymentMethod]);

    useEffect(() => {
        currency ? setDonationCurrency(currency) : setDonationCurrency("");
        amountValue ? setDonationAmount(amountValue) : setDonationAmount("");
        amountInBDT ? setDonationAmountInBDT(amountInBDT) : setDonationAmount("");
        // oneTime ? setDonationType(DonationType.ONE_TIME) : recurring ? setDonationType(DonationType.RECURRING) : setDonationType("");
        // monthly ? setDonationFrequency("Monthly") : yearly ? setDonationFrequency("Annually") : setDonationFrequency("");
        oneTime ? setDonationType(DonationType.ONE_TIME) : recurring ? setDonationType(DonationType.RECURRING) : setDonationType("");

        daily
            ? setDonationFrequency("Daily")
             :
              monthly
              ? setDonationFrequency("Monthly")
              : yearly
                ? setDonationFrequency("Annually")
                : setDonationFrequency("");

        setPaymentType(paymentMedia);
        if (amountInBDT) {
            if (amountInBDT > 1000000000) {
                setInvalidAmountError(ErrorMessages.InvalidAmountError);
                setInvalidAmount(true);
            } else {
                setInvalidAmountError("");
                setInvalidAmount(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oneTime, recurring, amountValue, amountInBDT, monthly, yearly, currency, paymentMedia]);

    // useEffect(() => {
    //     if (currencyRate) {
    //         setAmountInBDT(Math.round(Number((currencyRate * Number(amountValue)).toFixed(3))));
    //     }
    //     // eslint-disable-next-line
    // }, [amountValue, currencyRate]);
    useEffect(() => {
        console.log("currency", currency);
        if (currency && !currency.startsWith("BDT")) {
            getCurrencyRate();
        } else {
            setCurrencyRate(1);
            setSelectedAmount(2500); // Set the selected amount
            setAmountValue(String(2500)); // Update the amount value as string
            sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                selectedAmount: 2500,
            });
            handleChange(2500);
        }
    }, [currency]);

    useEffect(() => {
        if (currencyRate !== null) {
            setAmountInBDT(Math.round(Number((currencyRate * Number(amountValue)).toFixed(3))));
        }
    }, [currencyRate, amountValue]);
    const getCampaignData = async (campaignSlug: string) => {
        try {
            const { data: campaignDetails } = await getCampaignDetails(campaignSlug, APICallerRef.current, true);

            if (campaignDetails.recurring_supported_gateways.length > 0) {
                const isRecurringButtonPresent = paymentTypeButtons.some((item) => item.label === Constant.MONTHLY);
                if (!isRecurringButtonPresent) {
                    setPaymentTypeButtons((currentPaymentButtons) => [...currentPaymentButtons, recurringPaymentButtons]);
                }
            } else {
                setOneTime(true);
                updatePaymentButtonSelection(Constant.ONE_TIME as DonationType);
            }

            setRecurringSupportedGateways(campaignDetails.recurring_supported_gateways);
            setPreferredBankPayment(campaignDetails.bank_gateway ? campaignDetails.bank_gateway : "");
            setSupportNexusPayment(Boolean(campaignDetails.other_payments.includes(SupportedOtherPayment.NEXUSPAY)));
            setSupportAmexPayment(Boolean(campaignDetails.other_payments.includes(SupportedOtherPayment.AMEX)));
            setSupportBkashPayment(Boolean(campaignDetails.other_payments.includes(SupportedOtherPayment.BKASH)));
            setSupportNagadPayment(Boolean(campaignDetails.other_payments.includes(SupportedOtherPayment.NAGAD)));
            setSupportCashPayment(Boolean(campaignDetails.other_payments.includes(SupportedOtherPayment.CASH)));
            setSupportCheckPayment(Boolean(campaignDetails.other_payments.includes(SupportedOtherPayment.CHEQUE)));
        } catch (error: any) {
            // eslint-disable-next-line
        }
    };

    const handleChange = (value: any) => {
        if (value) setAmountValue(String(value));
    };

    const resetAllFrequencyButtons = () => {
        setMonthly(false);
        setYearly(false);
    };

    const updatePaymentButtonSelection = (buttonLabel: DonationType) => {
        setPaymentTypeButtons((paymentButtons) => {
            return paymentButtons.map((button) => {
                if (button.label === buttonLabel) {
                    button.isSelected = true;
                } else {
                    button.isSelected = false;
                }
                return button;
            });
        });
    };

    const handleButtonClick = (type: string, title: string) => {
        console.log(monthly, Constant.MONTHLY, type, title, "check");
        setPaymentMedia("");
        if (title === Constant.ONE_TIME) {
            if (!oneTime) {
                resetAllFrequencyButtons();
                setOneTime(true);
                setRecurring(false);
                updatePaymentButtonSelection(Constant.ONE_TIME as DonationType);
            }
            // } else if (title === Constant.RECURRING) {
            //     if (!recurring) {
            //         resetAllFrequencyButtons();
            //         setRecurring(true);
            //         setOneTime(false);
            //         updatePaymentButtonSelection(Constant.RECURRING as DonationType);
            //     }
        } else if (title === Constant.MONTHLY) {
            if (!monthly) {
                setRecurring(true);
                setOneTime(false);
                updatePaymentButtonSelection(Constant.RECURRING as DonationType);
                setMonthly(true);
                setYearly(false);
                setDaily(false);
            }
        } else if (title === Constant.YEARLY) {
            if (!yearly) {
                setRecurring(true);
                setOneTime(false);
                updatePaymentButtonSelection(Constant.RECURRING as DonationType);

                setMonthly(false);
                setDaily(false);
                setYearly(true);
            }
        } else if (title === Constant.DAILY) {
            if (!daily) {
                setRecurring(true);
                setOneTime(false);
                updatePaymentButtonSelection(Constant.RECURRING as DonationType);
                setDaily(true);
                setMonthly(false);
                setYearly(false);
            }
        } else if (type === "PaymentType") {
            setPaymentMedia(title);
        }
    };

    const getCurrencyCode = (currency: string) => {
        return currency.split(" ")[0]; // Extracts "USD" from "USD ($)"
    };

    const getCurrencyRate = async () => {
        const cleanCurrency = getCurrencyCode(currency);
        switch (cleanCurrency) {
            case "USD":
                setSelectedAmount(85); // Set the selected amount
                setAmountValue(String(85)); // Update the amount value as string
                sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                    selectedAmount: 85,
                });
                handleChange(85); // Set donation amount for USD
                break;
            case "INR":
                setSelectedAmount(8000); // Set the selected amount
                setAmountValue(String(8000)); // Update the amount value as string
                sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                    selectedAmount: 8000,
                });
                handleChange(8000); // Set donation amount for INR
                break;
            case "BDT":
                setSelectedAmount(2500); // Set the selected amount
                setAmountValue(String(2500)); // Update the amount value as string
                sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                    selectedAmount: 2500,
                });
                handleChange(2500);
            case "AUD":
                setSelectedAmount(150); // Set the selected amount
                setAmountValue(String(150)); // Update the amount value as string
                sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                    selectedAmount: 150,
                });
                handleChange(150); // Set donation amount for AUD
                break;
            case "CAD":
                setSelectedAmount(115); // Set the selected amount
                setAmountValue(String(115)); // Update the amount value as string
                sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                    selectedAmount: 115,
                });
                handleChange(115); // Set donation amount for CAD
                break;
            case "EUR":
                setSelectedAmount(80); // Set the selected amount
                setAmountValue(String(80)); // Update the amount value as string
                sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                    selectedAmount: 80,
                });
                handleChange(80); // Set donation amount for EUR
                break;
            case "GBP":
                setSelectedAmount(70); // Set the selected amount
                setAmountValue(String(70)); // Update the amount value as string
                sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                    selectedAmount: 70,
                });
                handleChange(70); // Set donation amount for GBP
                break;
            case "JPY":
                setSelectedAmount(12250); // Set the selected amount
                setAmountValue(String(12250)); // Update the amount value as string
                sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                    selectedAmount: 12250,
                });
                handleChange(12250); // Set donation amount for JPY
                break;
            default:
                setSelectedAmount(2500); // Set the selected amount
                setAmountValue(String(2500)); // Update the amount value as string
                sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
                    selectedAmount: 2500,
                });
                handleChange(2500); // Default case
                break;
        } // Extract clean currency code
        // Debugging log

        setcurrencyError(""); // Clear previous errors

        try {
            const data = await getConvertedCurrency(cleanCurrency, "BDT");
            setCurrencyRate(data);
            console.log("currencyrate", currencyRate);

            setAmountInBDT(Math.round(Number((data * Number(amountValue)).toFixed(3))));
        } catch (err: any) {
            console.log("Converted error", err);
            setcurrencyError(err.message || "Something went wrong!"); // Store error message
        }
    };

    /*************  ✨ Codeium Command ⭐  *************/
    /**
     * Formats a given number into the Indian numbering system.
     *
     * The function converts the number to a string, separates the last three digits,
     * and groups the remaining digits into pairs of two, adding commas as thousand separators.
     *
     * For example, 1234567 will be formatted as "12,34,567".
     *
     * @param amount - The number to be formatted.
     * @returns The formatted string representation of the number in the Indian numbering style.
     */

    /******  325dd3a5-7386-4386-b157-e1611c762c72  *******/
    function formatIndianNumbering(amount: number): string {
        const amountStr = amount.toString();

        // If the number has 3 or fewer digits, return it as is (no commas needed)
        if (amountStr.length <= 3) {
            return amountStr;
        }

        const lastThreeDigits = amountStr.slice(-3); // Get the last three digits
        const remainingDigits = amountStr.slice(0, amountStr.length - 3); // Get the digits before the last three

        // Group the remaining digits into pairs of two
        const formattedRemainingDigits = remainingDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",");

        // Combine the last three digits and the remaining formatted digits
        return `${formattedRemainingDigits},${lastThreeDigits}`;
    }

    // Number Validation Function
    const isValidNumber = (value: string) => {
        return /^\d*\.?\d*$/.test(value); // শুধু সংখ্যা এবং দশমিক অনুমোদিত
    };

    const textFieldRef = useRef<HTMLInputElement>(null);

    const handleOthersButtonClick = () => {
        setSelectedAmount(null); // Deselect predefined amounts
        setAmountValue(""); // Clear input field
        sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton);

        // Focus the TextField
        if (textFieldRef.current) {
            textFieldRef.current.focus();
        }
    };

    const handleAmountClick = (amount: number) => {
        setSelectedAmount(amount); // Set the selected amount
        setAmountValue(String(amount)); // Update the amount value as string
        sendEvent(GoogleAnalyticsEvents.PredefinedAmountButton, {
            selectedAmount: amount,
        });
        handleChange(amount); // Ensure to call your handler for further actions
    };

    return (
        <Box textAlign={"left"} sx={{ backgroundColor: "#fff", mx: 0, px: 0 }}>
            {children}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 1 }}>
                <svg
                    fill="none"
                    height="28"
                    viewBox="0 0 28 28"
                    width="28"
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon-fill shrink-0 font-size-28 text-green-80 me-2"
                    aria-hidden="true"
                >
                    <g fill="#00c04b">
                        {" "}
                        {/* Set color here */}
                        <path
                            clipRule="evenodd"
                            d="m15.0393 2.44995c-.6707-.24772-1.4079-.24772-2.0786 0l-8.30715 3.06797c-.39274.14504-.65355.51939-.65355.93807v7.54411c0 3.6861 1.95965 6.6874 4.28911 8.8073 1.16017 1.0557 2.38789 1.8689 3.45309 2.4137 1.1081.5668 1.9145.779 2.2578.779s1.1497-.2122 2.2578-.779c1.0652-.5448 2.2929-1.358 3.4531-2.4137 2.3294-2.1199 4.2891-5.1212 4.2891-8.8073v-7.54411c0-.41868-.2608-.79303-.6536-.93807zm-2.7715-1.876139c1.1179-.412868 2.3465-.412868 3.4644 0l8.3071 3.067969c1.1783.43514 1.9607 1.55819 1.9607 2.81421v7.54411c0 4.4389-2.3618 7.9375-4.943 10.2865-1.2952 1.1786-2.6702 2.092-3.8885 2.7151-1.1754.6012-2.3332.9984-3.1685.9984s-1.9931-.3972-3.1685-.9984c-1.21831-.6231-2.59328-1.5365-3.88847-2.7151-2.58125-2.349-4.94303-5.8476-4.94303-10.2865v-7.54411c0-1.25602.78243-2.37907 1.96066-2.81421z"
                            fillRule="evenodd"
                        />
                        <path d="m18.2906 11.75h-.2535v-1.1855c0-2.19278-1.7415-4.02451-3.9182-4.06361-.0595-.00107-.1783-.00107-.2378 0-2.1767.0391-3.91819 1.87083-3.91819 4.06361v1.1855h-.25354c-.39069 0-.70937.4028-.70937.9003v5.9463c0 .4969.31868.9035.7094.9035h8.5812c.3907 0 .7094-.4066.7094-.9035v-5.9463c0-.4974-.3187-.9003-.7094-.9003zm-3.4867 3.8674v1.7967c0 .2058-.1723.3799-.3784.3799h-.8509c-.2061 0-.3785-.1741-.3785-.3799v-1.7967c-.1999-.1966-.3162-.4684-.3162-.7691 0-.5698.4408-1.0594 1.0013-1.082.0594-.0024.1783-.0024.2377 0 .5605.0226 1.0013.5122 1.0013 1.082 0 .3007-.1164.5725-.3163.7691zm1.5623-3.8674h-4.7323v-1.1855c0-1.30621 1.0623-2.38623 2.3661-2.38623s2.3662 1.08002 2.3662 2.38623z" />
                    </g>
                </svg>
                <Typography
                    sx={{
                        px: 1,
                    }}
                    variant="h3"
                >
                    Secure Donation 
                </Typography>
            </Box>

            {/* <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} marginY={1.5}>
                <Grid
                    item
                    xs={12}
                    display={"flex"}
                    justifyContent={"center"}
                    width={"100px"}
                    sx={{
                        "&:hover": {
                            border: "none", // Ensure no border on hover
                        },
                    }}
                >
                    <SelectDropdown
                        label={currencyLabel}
                        dropdownContent={currencyDropdownContent}
                        value={currency}
                        setValue={(selectedCurrency) => {
                            sendEvent(GoogleAnalyticsEvents.CurrencyChangeEvent, { selectedCurrency });
                            setCurrency(selectedCurrency);
                        }}
                    />
                </Grid>
            </Box> */}

            <Box>
                {campaignSlug !== "stand-with-our-friend-imtiaz" && campaignSlug !== "sponsor-a-child" && (
                    <Typography
                        sx={{
                            px: 1,
                            pt: 1,
                            pb: 2,
                            textAlign: "center",
                            color: "#233455",
                        }}
                        variant="h5"
                    >
                        Support Thalassemia Patients With Zakat
                    </Typography>
                )}
                {(campaignSlug === "stand-with-our-friend-imtiaz" || campaignSlug === "sponsor-a-child") && (
                    <Typography
                        sx={{
                            px: 1,
                            pt: 1,
                            pb: 2,
                            textAlign: "center",
                            color: "#233455",
                        }}
                        variant="h5"
                    >
                        Choose an amount to give
                    </Typography>
                )}

                <Box>
                    {/* Currency Select Box */}
                    <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="center"
                        width="100%"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "4px",
                                "& fieldset": {
                                    border: "1px solid #ce1337",
                                },
                                "&:hover fieldset": {
                                    border: "1px solid #ce1337 !important",
                                    transition: "border 0.3s ease",
                                },
                                "&.Mui-focused fieldset": {
                                    border: "1px solid #ce1337",
                                },
                            },
                            "& .MuiInputLabel-root": {
                                color: "#465a7e", // Label color
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                                color: "#465a7e", // Label color when focused
                            },
                        }}
                    >
                        <SelectDropdown
                            dropdownContent={currencyDropdownContent}
                            value={currency}
                            label="Select Currency"
                            setValue={(selectedCurrency) => {
                                sendEvent(GoogleAnalyticsEvents.CurrencyChangeEvent, { selectedCurrency });
                                setCurrency(selectedCurrency);
                            }}
                        />
                    </Grid>
                </Box>

                <Box display={"flex"}>
                    <Grid
                        // textAlign={'center'}
                        item
                        xs={12}
                        // display="flex"
                        // justifyContent="center"
                        // alignItems="center" // Center vertically
                    >
                        <Box
                            sx={{
                                pr: 1.5,
                                width: "100%",
                                display: "grid",
                                pt: 2,
                                gridAutoFlow: "dense",
                                gridTemplateColumns: {
                                    xxs: "repeat(2, 1fr)", // ছোট স্ক্রিনে ২টি কলাম
                                    xs: "repeat(3, 1fr)",
                                    sm: "repeat(4, 1fr)",
                                    md: "repeat(4, 1fr)",
                                    lg: "repeat(4, 1fr)",
                                },
                                gap: 0.5,
                                justifyContent: "center",
                                alignItems: "center",
                                "@media (max-width: 767px)": {
                                    justifyContent: "flex-start",
                                    alignItems: "flex-start", // এখানে "left" পরিবর্তে "flex-start" ব্যবহার করো
                                },
                            }}
                        >
                            {selectedAmounts.map((amount: number) => (
                                <Button
                                    key={amount}
                                    variant="contained"
                                    sx={{
                                        fontSize: "14px",
                                        height: 40,
                                        fontWeight: "500",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignContent: "center",
                                        borderRadius: "4px",
                                        mb: 1,
                                        mr: { xs: 1 },
                                        background: selectedAmount === amount ? "#ce1337" : "#fff", // Change background color when selected
                                        color: selectedAmount === amount ? "#fff" : "#434E5A", // Change text color when selected
                                        border: selectedAmount === amount ? "1px solid #ce1337" : "1px solid #EBEDF3", // Change border color when selected
                                        "&:hover": {
                                            background: selectedAmount === amount ? "#ce1337" : "white",
                                            color: selectedAmount === amount ? "#fff" : "#434E5A",
                                            border: selectedAmount === amount ? "1px solid #ce1337" : "1px solid #ce1337", // Keep hover style consistent
                                        },
                                    }}
                                    onClick={() => handleAmountClick(amount)} // Use the new function to handle click
                                >
                                    <span style={{ fontSize: "16px", paddingRight: "2px" }}>{currency?.match(/\(([^)]+)\)/)?.[1] || ""}</span>{" "}
                                    {formatIndianNumbering(amount)}
                                </Button>
                            ))}
                            <Button
                                onClick={handleOthersButtonClick}
                                sx={{
                                    fontSize: "14px",
                                    width: 75,
                                    height: 38,
                                    fontWeight: "500",
                                    borderRadius: "4px",
                                    mb: 1,
                                    mr: { xs: 1 },
                                    background: selectedAmount === null ? "#ce1337" : "#fff", // Change background color when selected
                                    color: selectedAmount === null ? "#fff" : "#434E5A",
                                    border: selectedAmount === null ? "1px solid #ce1337" : "1px solid #EBEDF3",
                                    "&:hover": {
                                        background: selectedAmount === null ? "#ce1337" : "white",
                                        color: selectedAmount === null ? "#fff" : "#434E5A",
                                        border: "1px solid #ce1337",
                                    },
                                }}
                            >
                                Others
                            </Button>
                        </Box>
                    </Grid>
                </Box>
            </Box>
            <Box display="flex" flexDirection={{ xs: "row", sm: "row" }} marginY={1.5}>
                {/* SelectDropdown Section */}

                {/* TextField Section */}
                <Grid item display="flex" flexDirection="column" justifyContent="center" width="100%">
                    <TextField
                        id="outlined-number"
                        label="Amount"
                        type="string"
                        fullWidth
                        required
                        value={amountValue}
                        inputRef={textFieldRef} // Attach the ref to TextField
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            maxLength: 20,
                        }}
                        onFocus={() => setSelectedAmount(null)} // Input field select করলে Others inactive হবে
                        onChange={(event) => {
                            const newValue = event.target.value;
                            if (isValidNumber(newValue) || newValue === "") {
                                setAmountValue(newValue);
                            }
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "4px",
                                "& fieldset": {
                                    border: "1px solid #ce1337",
                                },
                                "&:hover fieldset": {
                                    border: "1px solid #ce1337 !important",
                                    transition: "border 0.3s ease",
                                },
                                "&.Mui-focused fieldset": {
                                    border: "1px solid #ce1337",
                                },
                            },
                            "& .MuiInputLabel-root": {
                                color: "#465a7e", // Label color
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                                color: "#465a7e", // Label color when focused
                            },
                        }}
                    />
                </Grid>
            </Box>
            <Typography color="error" sx={{ fontWeight: "bold" }}>
                {currencyError}
            </Typography>

            {/* <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} marginY={1.5}>
                <Grid item xs={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} width={"100%"}>
                    <TextField
                        id="outlined-number"
                        label="Amount To Donate"
                        type="string"
                        fullWidth
                        required
                        value={amountValue}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            maxLength: 20,
                        }}
                        onChange={(event) => {
                            const newValue = String(event.target.value);
                            if (isValidNumber(newValue) || newValue === "") {
                                setAmountValue(newValue);
                            }
                        }}
                    />
                </Grid>
            </Box> */}

            <Box mb={1.5} sx={{}}>
                {/* {currency === "BDT (৳)" && (
                <Alert severity="success" icon={false} sx={{ display: "flex", alignItems: "center", borderRadius: "4px", px: "10px" }}>
                    <Typography>
                        <Typography>{getNote(amountInBDT)}</Typography>
                    </Typography>
                </Alert>
                 )} */}
                {currency == "BDT (৳)" && campaignSlug !== "stand-with-our-friend-imtiaz" && (
                    <Alert severity="success" icon={false} sx={{ display: "flex", alignItems: "center", borderRadius: "4px", px: "10px", my: "5px" }}>
                        <Typography>{getNote(amountValue)}</Typography>
                    </Alert>
                )}
                {/* {currency !== "BDT (৳)" && (
                    <Alert severity="success" icon={false} sx={{ display: "flex", alignItems: "center", borderRadius: "4px", px: "10px" , my: "5px"}}>
                        <Typography>
                        {(campaignSlug !== "stand-with-our-friend-imtiaz") && (
                     
                            <Typography>{getNote(amountValue)}</Typography>
                        )}
                            
                        
                        </Typography>
                    </Alert>
                        )} */}
                {currency !== "BDT (৳)" && campaignSlug !== "stand-with-our-friend-imtiaz" && (
                    <Alert severity="success" icon={false} sx={{ display: "flex", alignItems: "center", borderRadius: "4px", px: "10px", my: "5px" }}>
                        <Typography>
                            <Typography>{getNote(amountValue)}</Typography>
                            {/* <Typography>{getNote2(amountInBDT)}</Typography> */}
                        </Typography>
                    </Alert>
                )}

                {currency !== "BDT (৳)" && (
                    <Alert severity="success" icon={false} sx={{ display: "flex", alignItems: "center", borderRadius: "4px", px: "10px", my: "5px" }}>
                        <Typography>
                            Equivalent amount in Bangladeshi Taka{" "}
                            <span style={{ fontWeight: 700 }}>(BDT {formatIndianNumbering(amountInBDT ?? 0)})</span> will be charged.
                            {/* <Typography>{getNote2(amountInBDT)}</Typography> */}
                        </Typography>
                    </Alert>
                )}

                <Typography color="error" sx={{ fontWeight: "bold" }}>
                    {invalidAmountError}
                </Typography>
            </Box>
            <Divider />
            <Box marginY={1} sx={{ backgroundColor: "rgba(206, 19, 55, .1)" }}>
                <Grid display={"flex"} justifyContent={"center"} gap={0}>
                    {paymentTypeButtons[0] && (
                        <div style={{ margin: 0, padding: 0, border: "none" }}>
                            <PaymentButton {...paymentTypeButtons[0]} />
                        </div>
                    )}
                    {paymentFrequencyButtons.map((button, index) => (
                        <div key={index} style={{ margin: 0, padding: 0 }}>
                            <PaymentButton
                                {...button}
                                sx={{
                                    backgroundColor: "#EBEDF3",
                                }}
                            />
                        </div>
                    ))}
                </Grid>
            </Box>
            {/* {recurring && (
                <Box marginY={1.5}>
                    <Grid display={"flex"} justifyContent={"flex-start"} ml={1}>
                        <Typography fontSize={"15px"}>Please select your recurring frequency.</Typography>
                    </Grid>
                    <Grid display={"flex"} justifyContent={"flex-start"}>
                        {paymentFrequencyButtons.map((button, index) => (
                            <PaymentButton key={index} {...button} />
                        ))}
                    </Grid>
                </Box>
            )} */}

            <Suspense fallback={<Skeleton variant="rectangular" width="100%" height={300} />}>
                <Typography
                    sx={{
                        px: 1,
                        mt: 2,
                    }}
                    variant="h5"
                >
                    Select payment method
                </Typography>

                {oneTime || monthly || yearly || daily ? (
                    <Box marginY={1} sx={{ pr: 0.5 }}>
                        <Box
                            display="grid"
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "repeat(3, 1fr)",
                                    sm: "repeat(4, 1fr)",
                                    md: "repeat(3, 1fr)",
                                },
                                "@media (max-width: 520px)": {
                                    gridTemplateColumns: "repeat(3, 1fr)",
                                },
                                "@media (max-width: 390px)": {
                                    gridTemplateColumns: "repeat(2, 1fr)",
                                },
                            }}
                        >
                            {((oneTime && supportBkashPayment) ||
                                (recurring && supportBkashPayment && recurringSupportedGateways.indexOf(PaymentTypes.BKASH) !== -1)) && (
                                <PaymentButton {...recurringPaymentButtons[2]} />
                            )}

                            {((oneTime && supportNagadPayment) ||
                                (recurring && supportNagadPayment && recurringSupportedGateways.indexOf(PaymentTypes.NAGAD) > 0)) && (
                                //                                     <Box sx={{ gridColumn: { xs: "span 2", sm: "span 1", md: "span 2" } }}>
                                //     <PaymentButton {...recurringPaymentButtons[4]} />
                                //   </Box>
                                <Box
                                    sx={{
                                        gridColumn: {
                                            xs: "span 2", // Spans 2 columns for extra small screens
                                            sm: "span 1", // Spans 1 column for small screens
                                            md: "span 2", // Spans 2 columns for medium screens
                                            "@media (max-width: 390px)": {
                                                gridColumn: "span 1", // Spans 2 columns for screens 390px or smaller
                                            },
                                        },
                                    }}
                                >
                                    <PaymentButton {...recurringPaymentButtons[4]} />
                                </Box>
                            )}

                            {((oneTime && supportNexusPayment) ||
                                (recurring && supportNexusPayment && recurringSupportedGateways.indexOf(PaymentTypes.CITY_BANK) !== -1)) && (
                                <PaymentButton {...recurringPaymentButtons[5]} />
                            )}
                            {((oneTime && preferredBankPayment) ||
                                (recurring &&
                                    preferredBankPayment &&
                                    recurringSupportedGateways.indexOf(preferredBankPayment as PaymentTypes) !== -1)) && (
                                <>
                                    <PaymentButton {...recurringPaymentButtons[0]} />
                                    <PaymentButton {...recurringPaymentButtons[1]} />
                                </>
                            )}
                            {((oneTime && supportAmexPayment) ||
                                (recurring && supportAmexPayment && recurringSupportedGateways.indexOf(PaymentTypes.AMEX) !== -1)) && (
                                <PaymentButton {...recurringPaymentButtons[3]} />
                            )}

                            {oneTime && (
                                <>
                                    {supportCashPayment && <PaymentButton {...oneTimePaymentButtons[0]} />}
                                    {supportCheckPayment && <PaymentButton {...oneTimePaymentButtons[1]} />}
                                </>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <Suspense fallback={<Skeleton variant="rectangular" width="100%" height={300} />}></Suspense>
                )}
            </Suspense>
            {recurring && paymentMedia === PaymentTypes.BKASH && (
                <Alert severity="warning" icon={false} sx={{ display: "flex", alignItems: "center", borderRadius: "4px", px: "10px" }}>
                    <Typography>You can cancel your subscription anytime via the bKash app's Subscription section.</Typography>
                </Alert>
            )}
        </Box>
    );
};

export default PaymentMethod;
